import { useState, useCallback } from 'react';

/**
 * Generic hook for managing dialog/modal state
 * @returns Dialog state and control functions
 */
export const useDialog = () => {
  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  return {
    open,
    handleOpen,
    handleClose,
    handleToggle,
    setOpen,
  };
};

/**
 * Hook for managing form dialog state with edit/create modes
 * @param initialValues - Initial form values
 * @returns Form dialog state and control functions
 */
export const useFormDialog = <T extends Record<string, any>>(initialValues: T) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<T>(initialValues);
  const [editingItem, setEditingItem] = useState<T | null>(null);

  const isEditing = editingItem !== null;

  const handleOpen = useCallback(
    (editData?: T) => {
      if (editData) {
        setForm(editData);
        setEditingItem(editData);
      } else {
        setForm(initialValues);
        setEditingItem(null);
      }
      setOpen(true);
    },
    [initialValues],
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    setForm(initialValues);
    setEditingItem(null);
  }, [initialValues]);

  const updateForm = useCallback((updates: Partial<T>) => {
    setForm((prev) => ({ ...prev, ...updates }));
  }, []);

  const resetForm = useCallback(() => {
    setForm(initialValues);
  }, [initialValues]);

  return {
    open,
    form,
    setForm,
    updateForm,
    resetForm,
    editingItem,
    isEditing,
    handleOpen,
    handleClose,
    setOpen,
  };
};

/**
 * Hook for managing confirmation dialog state
 * @returns Confirmation dialog state and control functions
 */
export const useConfirmDialog = <T = any>() => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<T | null>(null);

  const handleOpen = useCallback((itemData?: T) => {
    if (itemData !== undefined) {
      setData(itemData);
    }
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setData(null);
  }, []);

  const handleConfirm = useCallback(
    async (onConfirm: (data: T | null) => void | Promise<void>) => {
      await onConfirm(data);
      handleClose();
    },
    [data, handleClose],
  );

  return {
    open,
    data,
    handleOpen,
    handleClose,
    handleConfirm,
    setOpen,
    setData,
  };
};
